import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { dedupeCreativesById, useCreatives } from 'features/creatives';
import { parseCreativeIdDigests, useLocationParams } from 'hooks';
import type { CreativeEntry } from 'types/shared';
import { Network } from '../../../../constants';
import { isReuploadableCreative } from '../utils';

const EMPTY_IDS: string[] = [];

/**
 * Facebook `single_ad` creatives for `app`. The selected ids live in the
 * `creatives` URL param; every row seen from the API is remembered so a
 * selected row stays available (for the grid and the payload) while a name
 * filter or "selected only" hides it from the current list.
 */
const useUploadCreativesRows = (app: string | undefined) => {
  const { setValue } = useFormContext();
  const { flatCreatives, setFlatCreatives } = useLocationParams();
  const selectedCreatives = (useWatch({ name: 'selectedCreatives' }) as string[] | undefined) ?? EMPTY_IDS;
  const selectedOnly = (useWatch({ name: 'selectedCreativesOnly' }) as boolean | undefined) ?? false;
  const filterByName = (useWatch({ name: 'filterCreativeByName' }) as string[] | undefined) ?? EMPTY_IDS;
  const [knownRows, setKnownRows] = useState<Record<string, CreativeEntry>>({});

  // Ids restored from the URL on first render; fetched by `ids` since the
  // browse list may not contain them.
  const [urlCreativeIds] = useState(() => [...new Set(flatCreatives)]);
  const urlIdDigests = useMemo(() => parseCreativeIdDigests(urlCreativeIds), [urlCreativeIds]);

  const baseParams = { app, ad_type: 'single_ad', network: Network.FACEBOOK };

  const { data: apiRowsRaw, isLoading, isError, error } = useCreatives({
    config: { enabled: !!app },
    params: { ...baseParams, query: filterByName },
  });
  const { data: urlRowsRaw } = useCreatives({
    config: { enabled: !!app && urlIdDigests.length > 0 },
    params: { ...baseParams, ids: urlIdDigests },
  });
  // Only image/video can be reuploaded to X, so other types never reach the grid.
  const apiRows = useMemo(() => apiRowsRaw?.filter(isReuploadableCreative), [apiRowsRaw]);
  const urlRows = useMemo(() => urlRowsRaw?.filter(isReuploadableCreative), [urlRowsRaw]);

  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current || !urlCreativeIds.length) return;
    hasRestoredRef.current = true;
    setValue('selectedCreatives', urlCreativeIds);
  }, [setValue, urlCreativeIds]);

  useEffect(() => {
    const incoming = [...(apiRows ?? []), ...(urlRows ?? [])];
    if (!incoming.length) return;
    setKnownRows((prev) => {
      const next = { ...prev };
      incoming.forEach((row) => {
        next[row.id] = row;
      });
      return next;
    });
  }, [apiRows, urlRows]);

  const selectedRows = useMemo(
    () => selectedCreatives.map((id) => knownRows[id]).filter((row): row is CreativeEntry => !!row),
    [knownRows, selectedCreatives],
  );

  const allRows = useMemo(
    () => dedupeCreativesById([...(apiRows ?? []), ...selectedRows]),
    [apiRows, selectedRows],
  );

  const rows = useMemo(() => {
    if (!selectedOnly) return allRows;
    const selected = new Set(selectedCreatives);
    return allRows.filter((row) => selected.has(row.id));
  }, [allRows, selectedCreatives, selectedOnly]);

  const setSelection = useCallback(
    (ids: string[]) => {
      setValue('selectedCreatives', ids, { shouldDirty: true });
      setFlatCreatives(ids);
    },
    [setFlatCreatives, setValue],
  );

  return {
    rows,
    selectedRows,
    totalCount: allRows.length,
    selectedCreatives,
    setSelection,
    isLoading: isLoading && !allRows.length,
    isError,
    error,
  };
};

export default useUploadCreativesRows;
